import axios, { AxiosResponse } from 'axios';
import { wrapAxiosInstance } from '../cache/modules/axiosFactory';
import { clearScope } from '../cache/cachePublicMethods';
import { setupAuthInterceptor, retryWithBackoff } from '../common';
import { NAMESPACES } from '../configuration';
const BASE_URL = NAMESPACES.payments_api.payment_receipts;

// Create axios instance WITHOUT static token (fixes race condition)
const api = setupAuthInterceptor(axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
}));

// Cached API wrapper for this namespace/group (used selectively)
const cachedApi = wrapAxiosInstance(api, {
  namespace: 'payments_api',
  group: 'payment_receipts',
  // No defaultTtl enforced globally; we'll pass per-call ttl where needed
  hashLargeSegments: true,
  emitMeta: true,
});

// /GetPaymentReceiptById/<int:receipt_id>

export const getPaymentReceiptById = async (receiptId: number): Promise<AxiosResponse<any>> => {
    return await retryWithBackoff(() => 
        api.get(`/GetPaymentReceiptById/${receiptId}`)
    );
}

// /GetActivePaymentReceiptsByMesaId/<int:mesa_id>

export const getActivePaymentReceiptsByMesaId = async (mesaId: number): Promise<AxiosResponse<any>> => {
    return await retryWithBackoff(() =>
        api.get(`/GetActivePaymentReceiptsByMesaId/${mesaId}`)
    );
}

// /GetPaymentReceiptByPedidoId/<int:pedido_id>

export const getPaymentReceiptByPedidoId = async (pedidoId: number): Promise<AxiosResponse<any>> => {
    return await retryWithBackoff(() =>
        api.get(`/GetPaymentReceiptByPedidoId/${pedidoId}`)
    );
}

/**
 * 
 * @ns.route('/GetActivePaymentReceiptsByPedidoGroupId/<int:pedido_group_id>')
@ns.param('pedido_group_id', 'The pedido group identifier')
class GetActivePaymentReceiptsByPedidoGroupId(Resource):
    @ns.doc('get_active_payment_receipts_by_pedido_group_id')
    @jwt_required()
    def get(self, pedido_group_id):
        """
        Get all active payment receipts for a pedido group.
        
        Returns all active (non-cancelled) receipts associated with the specified pedido group.
        Works exactly like GetActivePaymentReceiptsByMesaId but for groups instead of tables.
        """
        try:
            # Verify the group exists
            pedido_group = PedidoGroup.query.get(pedido_group_id)
            if not pedido_group:
                return {'message': 'Grupo de pedidos no encontrado'}, 404
            
            # Get all active receipts for this group
            payment_receipts = PaymentReceipt.query.filter_by(
                pedido_group_id=pedido_group_id,
                active=True
            ).all()
            
            if not payment_receipts:
                return [], 200
                
            return ReceiptHelperV2.toDictList(payment_receipts), 200
            
        except Exception as e:
            ns.abort(500, e.__doc__, status="No se encontraron registros", statusCode="500")
 */

export const getActivePaymentReceiptsByPedidoGroupId = async (pedidoGroupId: number): Promise<AxiosResponse<any>> => {
    return await retryWithBackoff(() =>
        api.get(`/GetActivePaymentReceiptsByPedidoGroupId/${pedidoGroupId}`)
    );
}


// /GetPaymentReceiptForReview/<int:receipt_id>

export const getPaymentReceiptForReview = async (receiptId: number): Promise<AxiosResponse<any>> => {
    return await retryWithBackoff(() =>
        api.get(`/GetPaymentReceiptForReview/${receiptId}`)
    );
}

// # GetPaymentReciptsByLocalIdAndDateRange/<int:local_id>/<string:start_date>/<string:end_date> in format YYYY-MM-DD

export const getPaymentReciptsByLocalIdAndDateRange = async (localId: number, startDate: string, endDate: string): Promise<AxiosResponse<any>> => {
    return await retryWithBackoff(() =>
        api.get(`/GetPaymentReciptsByLocalIdAndDateRange/${localId}/${startDate}/${endDate}`)
    );
}


export const getPendingPaymentReceiptsByUserId = async (userId: number): Promise<AxiosResponse<any>> => {
    return await retryWithBackoff(() =>
        api.get(`/GetPendingPaymentReceiptsByUserId/${userId}`)
    );
};

// GetAllPaymentReceiptsFromSessionList/<local_id> and a payload with {session_ids: [int]}

export const getAllPaymentReceiptsFromSessionList = async (localId: number, sessionIds: number[]) => {
    return await api.post(`/GetAllPaymentReceiptsFromSessionList/${localId}`, {
        session_ids: sessionIds
    });
}

export const getPaymentReceiptsFromCurrentSessionByLocalId = async(localId:number) =>{
  return await cachedApi.get(`/GetPaymentReceiptsFromCurrentSessionByLocalId/${localId}`, undefined, { ttl: '3m' })
};

// /GetPaymentReceiptByTransactionId/<int:transaction_id>

export const getPaymentReceiptByTransactionId = async (transactionId: number): Promise<AxiosResponse<any>> => {
    return await retryWithBackoff(() =>
        api.get(`/GetPaymentReceiptByTransactionId/${transactionId}`)
    );
}


export const createPaymentReceiptByMesaIdV2 = async (
  mesaId: number, 
  splitType: string, 
  userId: number, 
  totalPersons: number,
  targetSelections?: string[] // NEW: Optional array of selection_ids to be paid
) => {
  const res = await api.post(`/CreatePaymentReceiptByMesaIdV2/${mesaId}`, {
    split_type: splitType,
    user_id: userId,
    total_persons: totalPersons,
    ...(targetSelections && targetSelections.length > 0 && { target_selections: targetSelections }) // Only include if provided
  });
  await cachedApi.clear();
  return res;
}

// /CreatePaymentReceiptByPedidoId/<int:pedido_id>

const LAST_PEDIDO_KEY = 'lastPaymentPedido';

interface LastPedidoRecord {
  id: number;
  time: number;
}

interface ApiResponse<T = any> {
  data: T | null;
  status: number;
}

/**
 * Create a payment receipt for a given pedido, but guard against
 * duplicate calls within 5 seconds.
 *
 * @param pedidoId    – the ID of the pedido to generate a receipt for
 * @param splitType   – type of split to apply on the receipt
 * @param userId      – ID of the user performing the operation
 * @param clientId    – (optional) ID of the client
 * @param clientName  – name of the client
 * @param pedidoType  – the type/category of the pedido
 * @returns           A promise resolving to an object with `{ data, status }`.
 */
export const createPaymentReceiptByPedidoIdV2 = async (
  pedidoId: number,
  splitType: string,
  userId: number,
  clientId: number | null = null,
  clientName: string,
  pedidoType: string
): Promise<ApiResponse> => {
  const now = Date.now();

  // Load last request record
  let last: LastPedidoRecord | null = null;
  try {
    last = JSON.parse(localStorage.getItem(LAST_PEDIDO_KEY) || 'null');
  } catch {
    last = null;
  }

  // If same pedidoId within 5 seconds, skip and return a dummy success
  if (last && last.id === pedidoId && now - last.time < 5_000) {
    console.warn(`Suppressed duplicate createPaymentReceipt for pedidoId ${pedidoId}`);
    return Promise.resolve({ data: null, status: 200 });
  }

  // Otherwise, record this call
  localStorage.setItem(
    LAST_PEDIDO_KEY,
    JSON.stringify({ id: pedidoId, time: now })
  );

  // Perform the real API call
  try {
  const response = await api.post(`/CreatePaymentReceiptByPedidoIdV2/${pedidoId}`, {
      split_type: splitType,
      user_id: userId,
      client_id: clientId,
      client_name: clientName,
      pedido_type: pedidoType,
    });
  // Invalidate cached payment receipts queries
  await cachedApi.clear();
  // Ensure we return only { data, status }
  return { data: response.data, status: response.status };
  } catch (err: any) {
    // Bubble up error shape consistently
    return Promise.reject({
      data: err.response?.data ?? null,
      status: err.response?.status ?? 200,
    });
  }
};
// /PrintReceiptById/<int:receipt_id>

/**
 * @ns.route('/CreatePaymentReceiptByPedidoGroupIdV2/<int:pedido_group_id>')
@ns.param('pedido_group_id', 'The pedido group identifier')
class CreatePaymentReceiptByPedidoGroupIdV2(Resource):
    @ns.doc('create_payment_receipt_by_pedido_group_id')
    @ns.expect(create_pedido_parser)
    @jwt_required()
    def post(self, pedido_group_id):
        """
        Create a payment receipt for a pedido group (bar/standing area).
        
        This endpoint creates receipts for all open pedidos associated with a pedido group.
        The group is automatically deactivated when all pedidos are fully paid.
        
        Works exactly like CreatePaymentReceiptByMesaIdV2 but for groups instead of tables.
        """
        data = create_pedido_parser.parse_args()
        data['pedido_group_id'] = pedido_group_id
        data['pedido_type'] = 'Pedido Group'
        receiptHelper = ReceiptHelperV2()
        result, message, status = receiptHelper.loaderController.loadData(data)
        return message, status
    
 */

export const createPaymentReceiptByPedidoGroupIdV2 = async (
  pedidoGroupId: number,
  splitType: string,
  userId: number,
  totalPersons: number = 1,
  clientId: number | null = null,
  clientName: string
) => {
  const res = await api.post(`/CreatePaymentReceiptByPedidoGroupIdV2/${pedidoGroupId}`, {
    split_type: splitType,
    user_id: userId,
    total_persons: totalPersons,
    client_id: clientId,
    client_name: clientName,
    pedido_type: 'Pedido Group'
  });
  await cachedApi.clear();
  return res;
}

export const printReceiptById = async (receiptId: number,workstationId:number | null) => {
    return await api.get(`/PrintReceiptById/${receiptId}/${workstationId}`, );
}

// /UpdatePaymentReceiptSuggestedTip/<int:receipt_id> that also receives a suggested_tip (float)

export const updatePaymentReceiptSuggestedTip = async (receiptId: number, suggestedTip: number) => {
  const res = await api.put(`/UpdatePaymentReceiptSuggestedTip/${receiptId}`, { suggested_tip: suggestedTip });
  await cachedApi.clear();
  return res;
}

// /UpdatePaymentReceiptTipPayed/<int:receipt_id>

export const updatePaymentReceiptTipPayed = async (receiptId: number, tipPayed: number) => {
  const res = await api.put(`/UpdatePaymentReceiptTipPayed/${receiptId}`, { tip_payed: tipPayed });
  await cachedApi.clear();
  return res;
}


export const applyClientMembershipToReceipt = async (receiptId: number, clientId: number) => {
  return await api.put(`/ApplyClientMembershipToReceipt/${receiptId}`, { client_id: clientId });
}

export const markPaymentReceiptAsRefunded = async (
  receiptId: number,
  refundType: number,
  workstationId?: number,
  description?: string
) => {
  if ((refundType === 1 || refundType === 2) && !workstationId) {
    throw new Error("El workstation_id es requerido");
  }
  if (refundType === 2 && !description) {
    throw new Error("La descripción es requerida para refund_type = 2");
  }

  const payload:any = {
    refund_type: refundType,
    // siempre incluimos la caja
    workstation_id: workstationId,
    // y la descripción sólo cuando toque
    ...(refundType === 2 && { description }),
  };

  try {
    const response = await api.put(
      `/MarkPaymentReceiptAsRefunded/${receiptId}`,
      payload
    );
  await cachedApi.clear();
    return response.data;
  } catch (error) {
    console.error("Error al marcar el recibo como reembolsado:", error);
    throw error;
  }
};



// /MarkPaymentReceiptAsPayed/<int:receipt_id>that receives a payload with user_id (int), client_id(int) and payment_type_id (int), and processor_name (string), from which only client can be null

export const markPaymentReceiptAsPayed = async (receiptId: number, userId: number, clientId: number | null, paymentTypeId: number, processorName: string) => {
  const res = await api.put(`/MarkPaymentReceiptAsPayed/${receiptId}`, {
    user_id: userId,
    client_id: clientId,
    payment_type_id: paymentTypeId,
    processor_name: processorName
  });
  await cachedApi.clear();
  return res;
}

export const markPaymentReceiptAsPayedV2 = async (
  receiptId: number, 
  userId: number, 
  clientId: number | null, 
  paymentTypeId: number, 
  processorName: string,
  multiplePayments?: { payment_type_id: number; amount: number; custom_payment_type_id?: number }[],
  customPaymentTypeId?: number | null,
  change?: number | null,  // NEW: Optional change amount for cash payments
  workstationId?: number | null,  // NEW: Optional workstation ID (auto-detected if not provided)
  approveCash?: boolean  // NEW: Approve cash transaction
) => {
  const payload: any = {
    user_id: userId,
    client_id: clientId,
    payment_type_id: paymentTypeId,
    processor_name: processorName,
    multiple_payment_methods: multiplePayments, // only send if multiple payments
    custom_payment_type_id: customPaymentTypeId // send custom payment type ID if provided
  };

  // Add optional cash payment parameters if provided
  if (change !== undefined && change !== null) {
    payload.change = change;
  }
  
  if (workstationId !== undefined && workstationId !== null) {
    payload.workstation_id = workstationId;
  }
  
  if (approveCash) {
    payload.approve_cash = true;
  }

  const res = await api.put(`/MarkPaymentReceiptAsPayedV2/${receiptId}`, payload);
  
  // Clear both the payments_api/payment_receipts cache and management_api/salones
  await cachedApi.clear();
  await clearScope({ namespace: 'management_api', group: 'salones' });
  
  return res;
};

export const markPaymentReceiptAsPartiallyPayed = async (receiptId: number, userId: number, clientId: number | null, paymentTypeId: number, processorName: string, amount:number, close:boolean=false, customPaymentTypeId?: number | null) => {
  const res = await api.put(`/MarkPaymentReceiptAsPartiallyPayed/${receiptId}`, {
    user_id: userId,
    client_id: clientId,
    payment_type_id: paymentTypeId,
    processor_name: processorName,
    amount: amount,
    close: close,
    custom_payment_type_id: customPaymentTypeId
  });
  await cachedApi.clear();
  return res;
}

// /UpdateDiscountForReceipt/<int:receipt_id>
// Supports two modes:
// 1. discount_id: Apply a pre-configured discount (scope-aware)
// 2. discount_amount: Apply a custom amount (legacy)
// Cannot provide both - they are mutually exclusive

export const updateDiscountForReceipt = async (
  receiptId: number, 
  discountAmount?: number, 
  comment?: string,
  discountId?: number
) => {
  const payload: any = {};
  
  // Mutually exclusive: either discount_id or discount_amount
  if (discountId !== undefined) {
    payload.discount_id = discountId;
  } else if (discountAmount !== undefined) {
    payload.discount_amount = discountAmount;
  }
  
  if (comment !== undefined && comment !== null) {
    payload.comment = comment;
  }
  
  const res = await api.put(`/UpdateDiscountForReceipt/${receiptId}`, payload);
  await cachedApi.clear();
  return res;
}


export const removeDiscountFromReceipt = async (receiptId: number, discountAmount?: number, discountId?: number) => {
  const payload:any = {};
  if (discountAmount !== undefined) {
    payload.discount_amount = discountAmount;
  }
  if (discountId !== undefined) {
    payload.discount_id = discountId;
  }
  const res = await api.put(`/RemoveDiscountFromReceipt/${receiptId}`, payload);
  await cachedApi.clear();
  return res;
}

// /MarkPaymentReceiptAsCancelled/<int:receipt_id

export const markPaymentReceiptAsCancelled = async (receiptId: number, comment?:string) => {
  const res = await api.put(`/MarkPaymentReceiptAsCancelled/${receiptId}`, { comment: comment });
  await cachedApi.clear();
  return res;
}

// /DeleteActivePaymentReceiptsByMesaId/<int:mesa_id>

export const deleteActivePaymentReceiptsByMesaId = async (mesaId: number) => {
  const res = await api.delete(`/DeleteActivePaymentReceiptsByMesaId/${mesaId}`);
  await clearScope({ namespace: 'payments_api', group: 'payment_receipts' });
  return res;
}

// /GetReceiptActions/<int:receipt_id>

export const getReceiptActions = async (receiptId: number) => {
    return await api.get(`/GetReceiptActions/${receiptId}`, );
};
